import React, { useState } from 'react';
import {
  Modal,
  Button,
  Steps,
  Form,
  Input,
  Select,
  message,
  Space,
  Typography,
} from 'antd';
import {
  FormOutlined,
  RocketOutlined,
  CheckCircleOutlined,
  SearchOutlined,
  FileTextOutlined,
  HeartOutlined,
} from '@ant-design/icons';
import { gamificationApi } from '../../api';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

/**
 * チュートリアルモーダル
 * 新規登録ユーザーに表示され、アンケートと使い方ガイドを表示
 * ※ランクアップモーダルはダッシュボードツアー完了後にGamificationSummary経由で表示
 */
const TutorialModal = ({ visible, onComplete, userProfile }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [canSubmit, setCanSubmit] = useState(false);

  // フォームの変更を監視
  const handleFormChange = () => {
    // 少し遅延させてバリデーションを実行
    setTimeout(() => {
      form
        .validateFields([
          'how_did_you_know',
          'user_role',
          'interest_reasons',
          'monthly_articles',
        ])
        .then(() => {
          setCanSubmit(true);
        })
        .catch(() => {
          setCanSubmit(false);
        });
    }, 100);
  };

  const steps = [
    {
      title: 'ようこそ',
      icon: <HeartOutlined />,
    },
    {
      title: 'アンケート',
      icon: <FormOutlined />,
    },
  ];

  // ステップ0: ウェルカム画面
  const renderWelcome = () => (
    <div style={{ textAlign: 'center', paddingTop: 0, paddingBottom: 20 }}>
      <Title
        level={1}
        className="gradient-text"
        style={{ fontSize: 24, marginBottom: 16, paddingBottom: 8 }}
      >
        magicss へようこそ！
      </Title>
      <Paragraph
        style={{
          fontSize: 15,
          color: '#595959',
          marginBottom: 32,
          lineHeight: 1.6,
          fontWeight: 500,
        }}
      >
        magicss（マジックス）は、「AIO/SEOに最適化されたコンテンツ」を
        <br />
        AIと共創するための究極のツールです。
      </Paragraph>

      {/* magicssでできること */}
      <div
        className="intro-box"
        style={{
          maxWidth: 600,
          margin: '0 auto',
          padding: 24,
          background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
          borderRadius: 12,
          border: '1px solid #bae6fd',
          textAlign: 'left',
        }}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <CheckCircleOutlined
              style={{
                fontSize: 18,
                color: '#0ea5e9',
                marginTop: 2,
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontSize: 15,
                lineHeight: 1.6,
                background:
                  'linear-gradient(135deg, rgb(15, 170, 201) 0%, rgb(38, 126, 233) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontWeight: 500,
                display: 'inline',
              }}
            >
              プロが実務で使うSEOリサーチが5分で完了
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <CheckCircleOutlined
              style={{
                fontSize: 18,
                color: '#0ea5e9',
                marginTop: 2,
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontSize: 15,
                lineHeight: 1.6,
                background:
                  'linear-gradient(135deg, rgb(15, 170, 201) 0%, rgb(38, 126, 233) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontWeight: 500,
                display: 'inline',
              }}
            >
              方針は人間、作業はAIによる高度な「共創」
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <CheckCircleOutlined
              style={{
                fontSize: 18,
                color: '#0ea5e9',
                marginTop: 2,
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontSize: 15,
                lineHeight: 1.6,
                background:
                  'linear-gradient(135deg, rgb(15, 170, 201) 0%, rgb(38, 126, 233) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontWeight: 500,
                display: 'inline',
              }}
            >
              文体や分量などの「スタイル」を保存＆調整し、記事に反映
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <CheckCircleOutlined
              style={{
                fontSize: 18,
                color: '#0ea5e9',
                marginTop: 2,
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontSize: 15,
                lineHeight: 1.6,
                background:
                  'linear-gradient(135deg, rgb(15, 170, 201) 0%, rgb(38, 126, 233) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontWeight: 500,
                display: 'inline',
              }}
            >
              自社のデータや商品情報等を記事に組み込んで生成
            </span>
          </div>
        </Space>
      </div>

      <Paragraph
        style={{
          fontSize: 15,
          color: '#595959',
          marginTop: 24,
          marginBottom: 0,
          lineHeight: 1.6,
          fontWeight: 500,
        }}
      >
        アンケート回答後のチュートリアルでは
        <br />
        <text style={{ fontWeight: 600, color: '#0ea5e9' }}>
          「実際のリサーチ機能や記事の生成」を無料で
        </text>
        お試しいただけます。
      </Paragraph>
    </div>
  );

  // ステップ1: アンケート
  const renderSurvey = () => (
    <div style={{ padding: '20px 0' }}>
      <Title
        level={3}
        style={{ marginBottom: 8, textAlign: 'center', fontSize: 20 }}
      >
        簡単なアンケートにご協力ください
      </Title>
      <Paragraph
        style={{
          textAlign: 'center',
          marginBottom: 24,
          fontSize: 14,
          color: '#0ea5e9',
          fontWeight: 500,
        }}
      >
        回答後、ボーナスクレジットが付与されます
      </Paragraph>
      <Form
        form={form}
        layout="vertical"
        onValuesChange={handleFormChange}
        initialValues={{
          how_did_you_know: undefined,
          user_role: undefined,
          interest_reasons: [],
          monthly_articles: undefined,
          feedback: '',
        }}
      >
        <Form.Item
          label="magicssをどこで知りましたか？"
          name="how_did_you_know"
          rules={[{ required: true, message: '選択してください' }]}
        >
          <Select
            placeholder="選択してください"
            options={[
              { value: 'web_search', label: 'Web検索' },
              { value: 'social_media', label: 'SNS（X/Twitter、Facebook等）' },
              { value: 'friend_referral', label: '友人・知人の紹介' },
              { value: 'blog_article', label: 'ブログ・記事' },
              { value: 'advertisement', label: '広告' },
              { value: 'other', label: 'その他' },
            ]}
          />
        </Form.Item>

        <Form.Item
          label="最も当てはまる回答をお選びください"
          name="user_role"
          rules={[{ required: true, message: '選択してください' }]}
        >
          <Select
            placeholder="選択してください"
            options={[
              { value: 'business_manager', label: '事業会社の責任者' },
              { value: 'business_staff', label: '事業会社のご担当者' },
              { value: 'agency', label: '制作会社または支援会社' },
              { value: 'seo_director', label: 'SEOディレクター' },
              { value: 'writer_editor', label: 'ライター・編集者' },
            ]}
          />
        </Form.Item>

        <Form.Item
          label="興味を持っていただいたキッカケや要因を教えてください（複数選択可）"
          name="interest_reasons"
          rules={[{ required: true, message: '1つ以上選択してください' }]}
        >
          <Select
            mode="multiple"
            placeholder="選択してください（複数選択可）"
            options={[
              {
                value: 'seo_strengthening',
                label: 'SEOを強化しようと思っていた',
              },
              {
                value: 'in_house_production',
                label: '記事制作の内製化を考えていた',
              },
              {
                value: 'searching_ai_tool',
                label: 'AIライティングツールを探していた',
              },
              {
                value: 'unsolved_issues',
                label: '他のツールで解決しきれない課題がある',
              },
              {
                value: 'research_results',
                label: 'リサーチ結果を先に知って興味を持った',
              },
              { value: 'unique_info', label: '独自情報を組み込めること' },
              {
                value: 'style_management',
                label: '記事のスタイルを細かく管理できること',
              },
            ]}
          />
        </Form.Item>

        <Form.Item
          label="現在どのくらいSEO記事を作成していますか？（あなた、または部署で）"
          name="monthly_articles"
          rules={[{ required: true, message: '選択してください' }]}
        >
          <Select
            placeholder="選択してください"
            options={[
              { value: '0', label: 'まだ作成していない' },
              { value: '1-3', label: '月に1〜3本' },
              { value: '4-10', label: '月に4〜10本' },
              { value: '11-20', label: '月に11〜20本' },
              { value: '21-50', label: '月に21〜50本' },
              { value: '51+', label: '月に51本以上' },
            ]}
          />
        </Form.Item>

        <Form.Item label="ご意見・ご要望（任意）" name="feedback">
          <Input.TextArea
            rows={3}
            placeholder="改善してほしい点や、欲しい機能などがあればお聞かせください"
          />
        </Form.Item>
      </Form>
    </div>
  );

  const handleNext = async () => {
    if (currentStep === 1) {
      // アンケート送信して完了
      setLoading(true);
      try {
        const values = await form.validateFields();

        await gamificationApi.submitTutorialSurvey(values);

        // アンケート完了メッセージを表示し、ダッシュボードツアーへ進む
        // ※ランクアップモーダルはツアー完了後にGamificationSummary経由で表示
        message.success('ありがとうございました！続いて使い方をご案内します。');
        onComplete?.();
      } catch (error) {
        if (error.errorFields) {
          return; // バリデーションエラー
        }
        console.error('Failed to submit tutorial survey:', error);
        message.error('送信に失敗しました。もう一度お試しください。');
      } finally {
        setLoading(false);
      }
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    setCurrentStep(currentStep - 1);
  };

  return (
    <>
      <Modal
        open={visible}
        title={null}
        footer={null}
        closable={false}
        maskClosable={false}
        width={700}
        centered
        style={{ top: 20 }}
        bodyStyle={{
          padding: 0,
        }}
      >
        <div style={{ paddingTop: 24, paddingLeft: 24, paddingRight: 24 }}>
          <Steps
            current={currentStep}
            items={steps}
            style={{ marginBottom: 24 }}
            size="small"
          />
        </div>

        <div
          style={{
            maxHeight: '60vh',
            overflowY: 'auto',
            paddingLeft: 24,
            paddingRight: 24,
            paddingBottom: 16,
          }}
        >
          {currentStep === 0 && renderWelcome()}
          {currentStep === 1 && renderSurvey()}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '16px 24px',
            borderTop: '1px solid #f0f0f0',
            background: '#fff',
          }}
        >
          {currentStep > 0 && <Button onClick={handlePrev}>戻る</Button>}
          {currentStep === 0 && (
            <Button
              type="primary"
              onClick={handleNext}
              style={{ marginLeft: 'auto' }}
            >
              次へ
            </Button>
          )}
          {currentStep === 1 && canSubmit && (
            <Button
              type="primary"
              onClick={handleNext}
              loading={loading}
              style={{ marginLeft: 'auto' }}
            >
              送信して始める
            </Button>
          )}
        </div>
      </Modal>
    </>
  );
};

export default TutorialModal;
